import { useEffect, useMemo, useState } from "react";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { PageHeader, EmptyState } from "@/components/common";
import { Card } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { TrendingUp, Target } from "lucide-react";
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid, Legend,
  RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar,
} from "recharts";
import { subjectColor } from "@/lib/constants";
import ExamTypeToggle from "@/components/ExamTypeToggle";

export default function StudentAnalytics() {
  const [students, setStudents] = useState([]);
  const [studentId, setStudentId] = useState("");
  const [data, setData] = useState(null);
  const [examSel, setExamSel] = useState("");
  const [topicData, setTopicData] = useState(null);
  const [examType, setExamType] = useState("KTT");

  useEffect(() => { api.get("/students").then((r) => setStudents(r.data)); }, []);
  useEffect(() => { if (studentId) api.get(`/students/${studentId}/analytics`, { params: { exam_type: examType } }).then((r) => setData(r.data)).catch((e) => toast.error(apiError(e))); }, [studentId, examType]);
  useEffect(() => { const tl = data?.timeline; if (tl?.length) setExamSel(tl[tl.length - 1].result_id || ""); else setExamSel(""); }, [data]);
  useEffect(() => { if (examSel) api.get(`/results/${examSel}/topic-analysis`).then((r) => setTopicData(r.data)).catch(() => setTopicData(null)); else setTopicData(null); }, [examSel]);

  const trend = useMemo(() => data?.timeline?.map((t) => ({ name: t.exam_name, net: t.net, puan: t.score })) || [], [data]);
  const radar = useMemo(() => {
    if (!data?.latest) return [];
    const names = data.latest.per_subject_names || {};
    return Object.entries(data.latest.per_subject || {}).map(([k, v]) => ({ subject: names[k] || k, net: v }));
  }, [data]);

  const subjectTrends = useMemo(() => {
    if (!data?.timeline?.length) return [];
    return data.timeline.map((t) => ({ name: t.exam_name, ...t.per_subject }));
  }, [data]);
  const subjectKeys = data?.latest ? Object.keys(data.latest.per_subject || {}) : [];

  return (
    <div>
      <PageHeader title="Öğrenci Analizi" desc="Öğrencinin sınavlar arası gelişimi">
        <ExamTypeToggle value={examType} onChange={setExamType} tid="sa-type" />
      </PageHeader>
      <Card className="p-5 bg-card border-border mb-6">
        <div className="space-y-1.5 max-w-md"><Label>Öğrenci</Label>
          <Select value={studentId} onValueChange={setStudentId}><SelectTrigger data-testid="sa-student"><SelectValue placeholder="Öğrenci seçin" /></SelectTrigger>
            <SelectContent className="bg-card border-border max-h-72">{students.map((s) => <SelectItem key={s.id} value={s.id}>{s.grade}-{s.branch} · {s.number} · {s.first_name} {s.last_name}</SelectItem>)}</SelectContent></Select>
        </div>
      </Card>

      {!data ? <EmptyState icon={TrendingUp} title="Öğrenci seçin" desc="Gelişim grafiğini görmek için bir öğrenci seçin." />
        : data.timeline.length === 0 ? <EmptyState icon={TrendingUp} title="Sonuç yok" desc={`${data.student.first_name} için henüz sınav sonucu bulunmuyor.`} />
        : (
          <>
            <Card className="p-5 bg-card border-border mb-4">
              <h3 className="font-semibold mb-1">{data.student.first_name} {data.student.last_name}</h3>
              <p className="text-sm text-muted-foreground mb-4">{data.student.grade}-{data.student.branch} · {data.timeline.length} sınav</p>
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={trend}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                  <XAxis dataKey="name" tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} />
                  <YAxis yAxisId="net" orientation="left" tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} stroke="hsl(var(--primary))" />
                  <YAxis yAxisId="puan" orientation="right" tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} stroke="hsl(var(--accent))" />
                  <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8 }} />
                  <Legend />
                  <Line yAxisId="net" type="monotone" dataKey="net" stroke="hsl(var(--primary))" strokeWidth={2.5} name="Net" dot={{ r: 4 }} />
                  <Line yAxisId="puan" type="monotone" dataKey="puan" stroke="hsl(var(--accent))" strokeWidth={2} name="Puan" dot={{ r: 3 }} />
                </LineChart>
              </ResponsiveContainer>
            </Card>

            <div className="grid lg:grid-cols-2 gap-4">
              <Card className="p-5 bg-card border-border">
                <h3 className="font-semibold mb-4">Ders Dengesi (Son Sınav)</h3>
                <ResponsiveContainer width="100%" height={320}>
                  <RadarChart data={radar}>
                    <PolarGrid stroke="hsl(var(--border))" />
                    <PolarAngleAxis dataKey="subject" tick={{ fontSize: 10, fill: "hsl(var(--muted-foreground))" }} />
                    <PolarRadiusAxis tick={{ fontSize: 10 }} />
                    <Radar dataKey="net" stroke="hsl(var(--primary))" fill="hsl(var(--primary))" fillOpacity={0.4} name="Net" />
                    <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8 }} />
                  </RadarChart>
                </ResponsiveContainer>
              </Card>
              <Card className="p-5 bg-card border-border">
                <h3 className="font-semibold mb-4">Ders Bazlı Net Gelişimi</h3>
                <ResponsiveContainer width="100%" height={320}>
                  <LineChart data={subjectTrends}>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                    <XAxis dataKey="name" tick={{ fontSize: 10, fill: "hsl(var(--muted-foreground))" }} />
                    <YAxis tick={{ fontSize: 10, fill: "hsl(var(--muted-foreground))" }} />
                    <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8 }} />
                    {subjectKeys.map((k) => <Line key={k} type="monotone" dataKey={k} stroke={subjectColor(k).hex} strokeWidth={2} dot={false} name={data.latest.per_subject_names[k]} />)}
                  </LineChart>
                </ResponsiveContainer>
              </Card>
            </div>

            <Card className="p-5 bg-card border-border mt-4" data-testid="topic-analysis-card">
              <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
                <h3 className="font-semibold flex items-center gap-2"><Target className="h-4 w-4 text-primary" />Konu Bazlı Analiz — Zayıf Konular</h3>
                <Select value={examSel} onValueChange={setExamSel}>
                  <SelectTrigger className="w-56" data-testid="ta-exam"><SelectValue placeholder="Sınav seçin" /></SelectTrigger>
                  <SelectContent className="bg-card border-border max-h-72">
                    {data.timeline.map((t) => <SelectItem key={t.result_id} value={t.result_id}>{t.exam_name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              {!topicData?.has_questions ? (
                <div className="text-sm text-muted-foreground py-8 text-center">
                  Bu sınav için <b>soru-konu bilgisi</b> girilmemiş. Soru Yönetimi'nden her soruya konu ekleyin; konu bazlı analiz otomatik oluşur.
                </div>
              ) : (
                <div className="space-y-2" data-testid="ta-list">
                  {topicData.topics.map((t, i) => {
                    const c = subjectColor(t.subject_key);
                    const weak = t.success < 50;
                    return (
                      <div key={i} className="flex items-center gap-3" data-testid={`ta-row-${i}`}>
                        <div className="w-40 shrink-0">
                          <div className="text-sm font-medium truncate">{t.topic}</div>
                          <div className="text-[11px] text-muted-foreground">{t.subject_name}{t.kazanim ? ` · ${t.kazanim}` : ""}</div>
                        </div>
                        <div className="flex-1 h-5 rounded bg-muted/50 overflow-hidden relative">
                          <div className="h-full rounded transition-all" style={{ width: `${t.success}%`, background: weak ? "#dc2626" : t.success < 75 ? "#f59e0b" : "#059669" }} />
                        </div>
                        <div className="w-12 text-right text-sm font-bold font-mono">%{t.success}</div>
                        <div className="w-28 text-right text-[11px] font-mono text-muted-foreground">D{t.correct}/Y{t.wrong}/B{t.blank}</div>
                      </div>
                    );
                  })}
                  <p className="text-xs text-muted-foreground pt-2">Kırmızı: %50 altı (öncelikli tekrar) · Turuncu: %50–75 · Yeşil: %75+. En zayıf konu en üstte.</p>
                </div>
              )}
            </Card>
          </>
        )}
    </div>
  );
}
